import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IonContent, IonIcon, IonLabel, IonPage, IonSpinner, useIonToast } from '@ionic/react';
import { eye, eyeOff, downloadOutline, laptopOutline, timeOutline, phonePortraitOutline, globeOutline } from 'ionicons/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { InstallBanner } from '../components/InstallBanner';
import { authService } from '../services/api/authService';
import { setAuth, setLoading, setError, clearAuth } from '../stores/authSlice';
import { RootState, persistor } from '../stores';
import { useQueryClient } from '@tanstack/react-query';
import { clearOfflineUserData } from '../services/offline/clear-offline-data-on-logout';
import styles from './Login.module.css';
import ilpLogoColor from '../assets/ilp_logo_color.png';

const asset = (name: string) => `${process.env.PUBLIC_URL || ''}/assets/login/${name}`;

const REMEMBER_STORAGE_KEY = 'b2b_login_remember';

// Only the email is persisted — never the password.
// The "Remember me" feature pre-fills the email field for convenience;
// authentication is handled via the refresh token stored in Redux (localForage).
interface StoredCredentials {
  email: string;
}

function loadRememberedCredentials(): StoredCredentials | null {
  try {
    const raw = localStorage.getItem(REMEMBER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'email' in parsed &&
      typeof (parsed as StoredCredentials).email === 'string'
    ) {
      return { email: (parsed as StoredCredentials).email };
    }
  } catch {
    // ignore invalid stored data
  }
  return null;
}

function saveRememberedCredentials(email: string): void {
  try {
    localStorage.setItem(REMEMBER_STORAGE_KEY, JSON.stringify({ email }));
  } catch {
    // ignore quota / private mode
  }
}

function clearRememberedCredentials(): void {
  try {
    localStorage.removeItem(REMEMBER_STORAGE_KEY);
  } catch {
    // ignore
  }
}

const BUBBLE_COUNT = 28;
const STAT_CARDS = [
  { icon: laptopOutline, value: '100%', label: 'Digitale' },
  { icon: timeOutline, value: '24/7', label: 'Disponibile' },
  { icon: globeOutline, value: 'Web', label: '& Mobile' },
] as const;
const FEATURE_ITEMS = [
  'Gestione ordini in tempo reale',
  'Report e analisi dettagliate',
  'Sincronizzazione multi-dispositivo',
] as const;

export const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [presentToast] = useIonToast();
  // Stable ref so presentToast never causes handlePostLoginNavigation to recreate
  const presentToastRef = useRef(presentToast);
  useEffect(() => { presentToastRef.current = presentToast; });
  // Guard: prevent navigation from firing more than once per mount
  const hasNavigatedRef = useRef(false);
  const { canInstall, isStandalone, promptInstall } = usePWAInstall();

  useEffect(() => {
    const stored = loadRememberedCredentials();
    if (stored) {
      setEmail(stored.email);
      setRemember(true);
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('accepted') === '1') {
      presentToast({
        message: 'Account creato. Effettua il login.',
        duration: 4000,
        color: 'success',
        position: 'top',
      });
    }
  }, [searchParams, presentToast]);

  const queryClient = useQueryClient();
  const { isLoading, error, isAuthenticated, user, companies, selectedCompanyId } =
    useSelector((state: RootState) => state.auth);

  const isFormValid = useMemo(() => {
    return email.trim().length > 0 && password.trim().length > 0;
  }, [email, password]);

  const bubbles = useMemo(() => {
    return Array.from({ length: BUBBLE_COUNT }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: 10 + Math.random() * 32,
      duration: 8 + Math.random() * 10,
      delay: Math.random() * 20,
    }));
  }, []);

  const handlePostLoginNavigation = useCallback(() => {
    if (!user) return;
    const role = user.globalRole || user.b2bRole || user.companyRole;
    if (role === 'system-admin') {
      navigate('/systemAdmin', { replace: true });
      return;
    }
    if (role === 'company-admin') {
      navigate('/companyAdmin', { replace: true });
      return;
    }
    if (user.requiresCompanySelection && companies.length > 0) {
      navigate('/selectCompany', { replace: true });
      return;
    }
    const companyId = selectedCompanyId || companies[0]?.companyId;
    if (companyId) {
      navigate(`/b2b/${companyId}`, { replace: true });
    } else {
      presentToastRef.current({
        message: 'Nessuna azienda associata al tuo account',
        duration: 3000,
        color: 'warning',
        position: 'top',
      });
    }
  }, [user, companies, selectedCompanyId, navigate]);

  useEffect(() => {
    const redirectFlag = sessionStorage.getItem('b2b_redirecting_to_login');
    if (redirectFlag) {
      if (isAuthenticated) {
        dispatch(clearAuth());
      }
      // 500ms > 100ms (delay di UpdateFlowInit): garantisce che il check
      // aggiornamenti su /login veda ancora il flag e salti il dialog.
      setTimeout(() => {
        sessionStorage.removeItem('b2b_redirecting_to_login');
      }, 500);
      return;
    }
    if (isAuthenticated && user && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      handlePostLoginNavigation();
    }
  }, [isAuthenticated, user, dispatch, handlePostLoginNavigation]);

  useEffect(() => {
    if (error) {
      presentToast({
        message: error,
        duration: 5000,
        color: 'danger',
        position: 'top',
      });
      dispatch(setError(null));
    }
  }, [error, presentToast, dispatch]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      presentToast({
        message: 'Inserisci email e password',
        duration: 2000,
        color: 'warning',
        position: 'top',
      });
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await authService.login({ email, password });

      // Se l'utente che si sta loggando è diverso da quello precedente,
      // pulisce tutti i dati offline prima di caricare la nuova sessione.
      // Questo copre il caso: sessione scaduta (handleUnauthorized → solo clearAuth,
      // non clearOfflineUserData) + login con agente diverso.
      const previousSub = user?.sub;
      const newSub = response.user?.sub;
      if (previousSub && newSub && previousSub !== newSub) {
        persistor.pause();
        try {
          await clearOfflineUserData();
          queryClient.clear();
        } finally {
          persistor.persist();
        }
      }

      dispatch(setAuth({ ...response, rememberMe: remember }));
      if (remember) {
        saveRememberedCredentials(email);
      } else {
        clearRememberedCredentials();
      }
      presentToast({
        message: 'Login effettuato con successo',
        duration: 2000,
        color: 'success',
        position: 'top',
      });
    } catch (err: unknown) {
      dispatch(setError(err instanceof Error ? err.message : 'Errore durante il login'));
    }
  };

  return (
    <IonPage>
      <InstallBanner show={!canInstall && !isStandalone} />
      <IonContent fullscreen scrollY={false}>
        <div className={styles.page}>
          <div className={styles.gradientBar} aria-hidden />
          <div className={styles.glassOverlay} aria-hidden />
          <div className={styles.leftPanel}>
            <div className={styles.dotGrid} aria-hidden />
            <div className={styles.geometricAccent} aria-hidden />
            {bubbles.map((b) => (
              <div
                key={b.id}
                className={styles.bubble}
                aria-hidden
                style={{
                  left: `${b.left}%`,
                  width: b.size,
                  height: b.size,
                  animationDuration: `${b.duration}s`,
                  animationDelay: `-${b.delay}s`,
                }}
              />
            ))}
            <div className={styles.leftPanelOverlay} aria-hidden />
            <div className={styles.leftContent}>
              <div className={styles.badge}>Il tuo spazio di lavoro</div>
              <h2 className={styles.headline}>
                Ordini<br />
                <span className={styles.digitali}>Digitali</span>
              </h2>
              <p className={styles.description} style={{color: 'var(--ion-color-light)'}}>
                Gestisci il tuo business da qualsiasi dispositivo, ovunque tu sia.
              </p>
              <div className={styles.statCards}>
                {STAT_CARDS.map((card) => (
                  <div key={card.label} className={styles.statCard}>
                    <IonIcon icon={card.icon} className={styles.statCardIcon} />
                    <div className={styles.statCardValue}>{card.value}</div>
                    <div className={styles.statCardLabel}>
                      <IonLabel style={{color: 'var(--ion-color-light)'}}>{card.label}</IonLabel>
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.featureList}>
                {FEATURE_ITEMS.map((text) => (
                  <div key={text} className={styles.featureItem}>
                    <span className={styles.featureBullet} aria-hidden />
                    <p className={styles.featureText} style={{color: 'var(--ion-color-light)'}}>{text}</p>
                  </div>
                ))}
              </div>
            </div>
            <img src={asset('lei_transparent.png')} alt="" className={styles.personaggio} aria-hidden />
            <img src={ilpLogoColor} alt="ILP Consulting" className={styles.logoILP} />
          </div>

          <div className={styles.rightPanel}>
            <div className={styles.card}>
              <img src={asset('logo_agent.png')} alt="Agent" className={styles.logoAgent} />
              <div className={styles.formHeading}>
                <p className={styles.bentornato}>Bentornato!</p>
                <h1 className={styles.accediTitle}>Accedi al tuo account</h1>
                <div className={styles.accentBar} aria-hidden />
              </div>

              <form onSubmit={handleLogin} className={styles.form}>
                <div className={styles.field}>
                  <label htmlFor="login-email">Email</label>
                  <input
                    id="login-email"
                    type="email"
                    placeholder="nome@azienda.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className={styles.field}>
                  <label htmlFor="login-password">Password</label>
                  <div className={styles.passwordWrapper}>
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className={styles.eyeBtn}
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                      disabled={isLoading}
                    >
                      <IonIcon icon={showPassword ? eyeOff : eye} />
                    </button>
                  </div>
                </div>

                <div className={styles.rememberRow}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                    />
                    Ricordami
                  </label>
                  <Link to="/forgot-password" className={styles.forgotLink}>
                    Password dimenticata?
                  </Link>
                </div>

                <button
                  type="submit"
                  className={styles.signInBtn}
                  disabled={isLoading || !isFormValid}
                >
                  {isLoading ? (
                    <IonSpinner name="crescent" aria-label="Accesso in corso" style={{ width: 22, height: 22 }} />
                  ) : (
                    'Accedi'
                  )}
                </button>
              </form>

              {canInstall && (
                <div className={styles.downloadSection}>
                  <button
                    type="button"
                    className={styles.downloadBtn}
                    onClick={async (e) => {
                      e.preventDefault();
                      await promptInstall();
                    }}
                  >
                    <IonIcon icon={downloadOutline} size="16" />
                    Scarica l&apos;App
                  </button>
                </div>
              )}
              <div className={styles.logoILPCard}>
                <img src={ilpLogoColor} alt="ILP Consulting" />
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};
